import axios from "axios";
import { API } from "@/App";

export class ImageError extends Error {
  constructor(code, message) { super(message); this.code = code; }
}

const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

const loadViaImg = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new ImageError("unsupported", "unsupported")); };
  img.src = url;
});

// Downscale + re-encode any browser-decodable image to a small JPEG (512px, ~<150KB). Fixes EXIF rotation.
export async function compressImage(file, { maxDim = 512, quality = 0.86 } = {}) {
  const looksLikeImage = file.type.startsWith("image/") || /\.(heic|heif|jpe?g|png|webp|gif|bmp|avif)$/i.test(file.name || "");
  if (!looksLikeImage) throw new ImageError("unsupported", "Not an image");
  if (file.size > MAX_SOURCE_BYTES) throw new ImageError("too_large", "Source too large");
  let source;
  try {
    source = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    source = await loadViaImg(file); // throws ImageError("unsupported") for HEIC on Chrome etc.
  }
  const w0 = source.width || source.naturalWidth, h0 = source.height || source.naturalHeight;
  if (!w0 || !h0) throw new ImageError("unsupported", "Could not decode image");
  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w0 * scale));
  canvas.height = Math.max(1, Math.round(h0 * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#111";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (source.close) source.close();
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob) throw new ImageError("unsupported", "Could not encode image");
  return blob;
}

// Upload a compressed avatar; resolves {photoUrl, fileId, size}. photoUrl is a stable public /api/avatars/{id} path.
export async function uploadAvatar(userId, blob, onProgress) {
  const form = new FormData();
  form.append("file", blob, "avatar.jpg");
  const r = await axios.post(`${API}/team/${userId}/avatar`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
  });
  return r.data;
}

// Absolute URL for an <img>: handles legacy data:/https: values and the new relative /api/avatars path.
export function avatarSrc(photoUrl) {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`;
  return photoUrl;
}

export function describeUploadError(err) {
  if (err instanceof ImageError) {
    if (err.code === "unsupported") return "That file can't be read as an image here. Use a JPG, PNG or WebP (iPhone HEIC photos need to be exported as JPG).";
    if (err.code === "too_large") return "That file is over 25MB. Pick a smaller photo.";
  }
  if (!err?.response) return "Network problem while uploading — check your connection and try again.";
  const { status, data } = err.response;
  const detail = typeof data?.detail === "string" ? data.detail : "";
  if (status === 413) return detail || "The photo is too large for the server (limit 1MB).";
  if (status === 415) return detail || "Unsupported image format — use JPG, PNG or WebP.";
  if (status === 401 || status === 403) return "Your session expired — please sign in again.";
  if (status >= 500) return detail || "The server couldn't store the photo right now. Please try again in a moment.";
  return detail || `Upload failed (HTTP ${status}).`;
}
